import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Alert, Button, Checkbox, Container, Flex, Text, TextField } from "@printtat/ui";
import { setAccessToken } from "@/lib/authToken";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface LocationState {
  challengeToken?: string;
}

export const MfaChallengePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const challengeToken = state?.challengeToken;

  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // If we land here without a challenge token, the user either deep-linked or
  // refreshed past the 5-minute window. Bounce them back to /sign-in so they
  // can re-enter their password and pick up a fresh challenge.
  useEffect(() => {
    if (!challengeToken) {
      navigate("/sign-in", {
        replace: true,
        state: { error: "Your sign-in session expired. Please sign in again." },
      });
    }
  }, [challengeToken, navigate]);

  if (!challengeToken) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/auth/2fa/verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeToken, code, rememberDevice }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.code === "TOKEN_INVALID" || body.code === "TOKEN_EXPIRED") {
          navigate("/sign-in", {
            replace: true,
            state: { error: "Your sign-in session expired. Please sign in again." },
          });
          return;
        }
        throw new Error(body.message || "Verification failed");
      }
      const data = (await res.json()) as { accessToken: string };
      setAccessToken(data.accessToken);
      window.location.href = "/dashboard";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
      setSubmitting(false);
    }
  };

  return (
    <Container size="2" className="min-h-screen">
      <Flex align="center" justify="center" className="min-h-screen">
        <Flex direction="column" gap="6" className="w-full max-w-sm">
          <Flex direction="column" align="center" gap="2">
            <Text size="8" weight="bold" align="center">
              Two-factor authentication
            </Text>
            <Text size="2" color="gray" align="center">
              {useBackup
                ? "Enter one of your backup codes."
                : "Enter the 6-digit code from your authenticator app."}
            </Text>
          </Flex>

          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="4">
              <TextField
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
                placeholder={useBackup ? "xxxx-xxxx" : "123456"}
                autoComplete="one-time-code"
                inputMode={useBackup ? "text" : "numeric"}
                autoFocus
                required
              />
              {!useBackup && (
                <Checkbox
                  checked={rememberDevice}
                  onCheckedChange={(v) => setRememberDevice(v === true)}
                  label="Trust this device for 7 days"
                />
              )}
              {error && <Alert variant="error">{error}</Alert>}
              <Button type="submit" disabled={submitting || code.length < 6}>
                {submitting ? "Verifying…" : "Verify"}
              </Button>
              <Flex justify="center">
                <button
                  type="button"
                  onClick={() => {
                    setUseBackup((v) => !v);
                    setCode("");
                    setError(null);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {useBackup
                    ? "Use authenticator code instead"
                    : "Use a backup code instead"}
                </button>
              </Flex>
              <Flex justify="center">
                <Link
                  to="/sign-in"
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel and return to sign in
                </Link>
              </Flex>
            </Flex>
          </form>
        </Flex>
      </Flex>
    </Container>
  );
};
